
import EventEmitter from "wolfy87-eventemitter";
import {Utils} from "../common/Utils";
import Const from "./Const";
//import {BaseMixin} from "../mixin/BaseMixin";

/**
 * Base class for all subclasses from the framework.
 * Provides the following basic functionality:
 *   - event manager (trigger, bind events, forwarding, auto-bind)
 *   - i18n (dictionary, namespace)
 *   - inline methods (dynamically extends the object per instance)
 *
 * @example <caption>Using the BaseClassConfig object</caption>
 *
 *  let foreign = new BaseClass({on: {'base:foobar': console.log}}); // print forwarded event
 *
 *  let example = new BaseClass({
 *
 *    // define inline event handler per class instantiation (useful for Collection class)
 *    on: {
 *
 *      // completely the same like "example.on('foobar', ...)"
 *      foobar: (msg, next) => {
 *        alert(msg);
 *        if (next) alert(next);
 *      }
 *    },
 *
 *    // forward events to another instance, typically used for bubble up to a parent
 *    fwd: {
 *      prefix: 'base',
 *      target: foreign
 *    },
 *
 *    // using global translation object (optional)
 *    i18n: {
 *      // the dictionary should be normally set by the global variable "window[Const.I18N_VARNAME]"
 *      // where the dictionary for the language to be used is already set, e.g.
 *      // window[Const.I18N_VARNAME] = {'foobar.msg.welcome': 'Hello World'}
 *
 *      ns: 'foobar.msg' // define namespace that will be prepended to the key when using "t('welcome')" otherwise use "t('foobar.msg.welcome')"
 *    }
 *
 *    // translation object with directly set dictionary
 *    i18n: {
 *      dict: {
 *        'foobar.msg.welcome': 'Hello World' // can be used with t('foobar.msg.welcome')
 *      }
 *    }
 *
 *  });
 *
 *  // using emit method to fire an event
 *  example.emit('foobar', 'Hello world!');
 *  example.emit('foobar', 'Hello world!', 'And more.');
 *
 *  // trigger is an alias from emit, but accept an array which contains all arguments
 *  example.trigger('foobar', ['Hello world!']); // correct syntax
 *  example.trigger('foobar', 'Hello world!');   // no-array syntax only works for ONE argument
 *  example.trigger('foobar', ['Hello world!', 'And more.']); // correct syntax for more that one argument
 *
 */
export class BaseClass {

  /**
   * Configuration object for the {@link BaseClass} class.
   * @typedef {Object} BaseClassConfig
   * @property {Application} app - reference to the root application
   * @property {Object.<String, Function>} [on] - key-value map to bind automatically by using 'on' function, where key is event name and value the callback
   * @property {Object.<String, Function>} [methods] - key is function name und value a callback; will be used by {@link invoke}
   * @property {{target: BaseClass, prefix: String}} [fwd] - forward all events to the given class instance; every event name prepended with "prefix:"
   * @property {Object} [i18n] - i18n configuration object
   * @property {String} [i18n.ns=''] - namespace for all keys in this class (will be prepended by {@link t};
   * @property {String} [i18n.varname=Const.I18N_VARNAME] - property name to extract i18n dictionary from global window object: window[i18n.varname]
   * @property {Object<String,String>} [i18n.dict] - key value map of translations; if not set, tries to use window[i18n.varname]
   */

  /**
   * Initialize the class and set basic configuration for event manager and i18n.
   *
   * @param {BaseClassConfig} config
   */
  constructor(config = {}) {

    let args = Array.prototype.slice.call(arguments);
    if (args.length > 1) {
      args.unshift(true);
      config = $.extend.apply($, args);
    }

    /**
     * Instance of event manager
     * @type {EventEmitter}
     * @private
     */
    this._ee = new EventEmitter();
    /**
     * Origin configuration
     * @type {BaseClassConfig}
     */
    this.config = config;
    /**
     * Reference to the root application
     * @type {Application}
     */
    this.app = config.app;
    /**
     * Configuration for event forwarding
     * @type {{target: BaseClass, prefix: String}|null}
     * @private
     */
    this._fwd = config.fwd || null;
    /**
     * Configuration for functions to be called by {@link invoke}
     * @type {Object<String, Function>|{}}
     */
    this.methods = config.methods || {};
    /**
     * List of mixin instances
     * @type {Array<BaseMixin>}
     */
    this.mixins = this._initMixins(config.mixins);

    // register class event handler
    this._bindOn(config.on);

    /**
     * Contains the i18n configuration
     * @type {{dict?: Object, varname?: Object, ns?: Object}|{}}
     * @private
     */
    this._i18n = config.i18n || {};
    if (!this._i18n.ns) this._i18n.ns = '';
    if (!this._i18n.varname) this._i18n.varname = Const.I18N_VARNAME;
    if (!this._i18n.dict && window[this._i18n.varname]) {
      this._i18n.dict = window[this._i18n.varname];
    }
    /**
     * Checks if i18n configuration and translations are available
     * @type {boolean}
     */
    this.hasI18n = !!this._i18n.dict;
  }

  /**
   * Initialize the event binding behind the 'on' configuration
   * @param {Object} on - plain object where key is the event name and value a function
   * @private
   */
  _bindOn(on) {
    if (on) {
      for (let eventName in on) {
        if (on.hasOwnProperty(eventName)) {
          let fn = on[eventName];
          if (Utils.isString(fn)) {
            fn = this[fn].bind(this);
          }
          if (Utils.isFunction(fn)) {
            this.on(eventName, fn);
          }
        }
      }
    }
  }

  /**
   * Initializes all given mixin classes.
   * @param {Array<BaseMixin>} mixins - array of mixin classes
   * @return {Array<BaseMixin>}
   * @private
   */
  _initMixins(mixins) {

    if (!Array.isArray(mixins)) {
      return [];
    }

    let result = [];
    mixins.forEach((mixinClass) => {
/*
      TODO: not working with webpack
      if (!(mixinClass instanceof BaseMixin)) {
        console.log(mixinClass);
        throw "Invalid config for mixins: Class must extends from BaseMixin."
      }
*/
      let propName = mixinClass.getConfigName();
      let conf = this.config[propName] || {};
      conf.app = this.app;
      conf.parent = this;
      result.push(new mixinClass(conf));
    });

    return result;
  }

  /**
   * Creates a new instance from the class using the given config.
   * Alias for "new BaseClass()".
   * @param {BaseClassConfig} [config] - configuration passed to the constructor
   * @return {BaseClass}
   */
  static create(...config) {
    return new this(...config);
  }

  /**
   * Checks given key exists in i18n dictionary
   * @param {String} key - i18n key
   * @return {boolean}
   */
  hasText(key) {
    return this.hasI18n && this._i18n.dict.hasOwnProperty(this._i18n.ns + key);
  }

  /**
   * Returns the translation / text for the given key from the dictionary.
   * Key will be returned if not found.
   * @param {String} key - i18n key
   * @return {String}
   */
  t(key) {
    if (!this.hasI18n) return key;
    let nsKey = this._i18n.ns + key;
    return this._i18n.dict[nsKey] ? this._i18n.dict[nsKey] : (this._i18n.dict[key] ? this._i18n.dict[key] : key);
  }

  /**
   * Adds the given listener function (cb) to the specific event.
   * @param {String} ev - event name
   * @param {Function} cb - callback function
   * @return {BaseClass}
   */
  on(ev, cb) {
    this._ee.on(ev, cb);
    return this;
  }

  /**
   * Same as 'on'. It will add a listener that will be automatically removed after its first execution.
   * @param {String} ev - event name
   * @param {Function} cb - callback function
   * @return {BaseClass}
   */
  once(ev, cb) {
    this._ee.once(ev, cb);
    return this;
  }

  /**
   * Removes a listener function from the specified event.
   * @param {String} ev - event name
   * @param {Function} cb - callback function
   * @return {BaseClass}
   */
  off(ev, cb) {
    this._ee.on(ev, cb);
    return this;
  }

  /**
   * Emits an event with any number of arguments to be passed to each listener.
   * Forwards events if configured in {@link BaseClassConfig}
   * @param {String} ev - Name of the event to emit and execute listeners for.
   * @param {*...} args - any number of arguments
   * @return {BaseClass}
   */
  emit(ev, ...args) {
    this._ee.emit.apply(this._ee, arguments);
    this._fwdEvent(ev, args);
    return this;
  }

  /**
   * Same as emit, but expects all arguments to be passed to a listener as an array.
   * @param {String} eventName - Name of the event to emit and execute listeners for.
   * @param {Array<*>} [args] - array of arguments; converted into an array if necessary
   * @return {BaseClass}
   */
  trigger(eventName, args) {

    // consistently array usage
    if (!Utils.isUndefOrNull(args) && !Array.isArray(args)) {
      args = [args];
    }

    this._ee.emitEvent(eventName, args);
    this._fwdEvent(eventName, args);
    return this;
  }

  /**
   * Triggers the given event with the arguments on the target class defined in 'fwd' configuration
   * @param {String} ev - event name
   * @param {Array} args - array of arguments
   * @private
   */
  _fwdEvent(ev, args) {
    if (this._fwd) {
      this._fwd.target.trigger(this._fwd.prefix + ':' + ev, [this._fwd.target, ...args]);
    }
  }

  /**
   * Searching the given function name in the 'methods' configuration {@link BaseClassConfig} and execute if found.
   * Otherwise tries to call the function at the instance.
   * @throws if the function name doesn't exist in 'methods' config nor class instance.
   * @param {String} fnName - name of function
   * @param {Array} args - array arguments passed to the function
   * @return {*}
   */
  invoke(fnName, args) {

    if (Utils.isFunction(this.methods[fnName])) {
      return this.methods[fnName].apply(this, args);
    }

    if (Utils.isFunction(this[fnName])) {
      return this[fnName].apply(this, args);
    }

    return null;
  }

  /**
   * Adds the given listener function (handler) to the specific event
   * on the foreign class.
   * @todo rethink: handler should be a string defined in the source class
   * @param {BaseClass} clazz - foreign class
   * @param {String} eventName - name of the event
   * @param {Function} handler - callable function
   */
  listenTo(clazz, eventName, handler) {
    clazz.on(eventName, handler);
  }

  /**
   * Remove the listener from the foreign class instance.
   * @todo rethink, see {@link listenTo}
   * @param {BaseClass} clazz - foreign class
   * @param {String} eventName - name of the event
   * @param {Function} handler - callable function
   */
  stopListening(clazz, eventName, handler) {
    clazz.off(eventName, handler);
  }

  /**
   * Call class function `fnName` and triggers an event on the class.
   * By default the `eventName` is the same like 'fnName`. The event name
   * will be transformed from camel case to double dot case,
   * e.g. fooBar -> foo:bar
   * @param {string} fnName - name of the function to be called
   * @param {string} [eventName] - name of the event to be triggered
   * @param {Array} [args] - array of arguments to be passed to the function and event handler
   * @return {*}
   */
  triggerMethod(fnName, eventName, args) {

    if (!Utils.isString(eventName)) {
      args = eventName;
      eventName = fnName;
    }
    let result;

    // transform camelcase to ":"-case
    eventName = eventName.replace(/([A-Z])/g, (g) => `:${g[0].toLowerCase()}`);
    this.trigger(eventName, args);

    if (Utils.isFunction(this[fnName])) {
      result = this[fnName].apply(this, args);
    }
    return result;
  }

  /**
   * Same as 'triggerMethod', but prepend 'on' to the given name. First character from
   * name will be uppercase to ensure camel-case syntax, eg. "beforeInit" -> "onBeforeInit".
   * @param {String} name - event/function name in camel case syntax
   * @param {Array} [args] - array of arguments to be passed to the function and event handler
   * @return {*}
   */
  triggerMethodOn(name, args) {
    let fnName = 'on' + name.charAt(0).toUpperCase() + name.substring(1);
    return this.triggerMethod(fnName, name, args);
  }
}
